import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Card, Form } from "react-bootstrap";
import Spinner from "../generics/Spinner";
import moment from "moment";

const getStatusStyle = (status) => {
  const styles = {
    ACTIVE: { backgroundColor: "#198754", color: "#fff" },
    INACTIVE: { backgroundColor: "#6c757d", color: "#fff" },
    MAINTENANCE: { backgroundColor: "#ffc107", color: "#000" },
    RETIRED: { backgroundColor: "#343a40", color: "#fff" },
  };
  return styles[status] || styles.INACTIVE;
};

const MachinesList = ({
  machines,
  machineTypes,
  locations,
  loading,
  error,
  onFilter,
}) => {
  const [filters, setFilters] = useState({
    search: "",
    machineType: "",
    location: "",
    status: "",
  });

  const handleFilterChange = (field, value) => {
    const newFilters = { ...filters, [field]: value };
    setFilters(newFilters);
    if (onFilter) {
      onFilter(newFilters);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    if (onFilter) {
      onFilter(filters);
    }
  };

  const clearFilters = () => {
    const clearedFilters = { search: "", machineType: "", location: "", status: "" };
    setFilters(clearedFilters);
    if (onFilter) {
      onFilter(clearedFilters);
    }
  };

  if (loading && machines.length === 0) {
    return <Spinner />;
  }

  return (
    <div className="machines-list">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h4 className="mb-0">
          <i className="fa fa-cogs me-2"></i>
          Machines
        </h4>
        <Link to="/maintenance/machines/new" className="btn btn-primary btn-sm">
          <i className="fa fa-plus me-1"></i> Add Machine
        </Link>
      </div>

      {/* Filters */}
      <div className="card mb-3">
        <div className="card-header reports-filter">
          <Form onSubmit={handleSearch} className="row align-items-center mb-0">
            <div className="col-12 col-sm-3">
              <label className="col-form-label filter-label" htmlFor="machineSearch">
                <i className="fa fa-search mr-05 ml-05"></i>
                Search
              </label>
              <input
                type="text"
                id="machineSearch"
                className="form-control"
                placeholder="Name, serial, model..."
                value={filters.search}
                onChange={(e) => handleFilterChange("search", e.target.value)}
              />
            </div>
            <div className="col-12 col-sm-3">
              <label className="col-form-label filter-label ml-05">
                Type
              </label>
              <select
                className="form-control"
                value={filters.machineType}
                onChange={(e) => handleFilterChange("machineType", e.target.value)}
              >
                <option value="">All Types</option>
                {machineTypes.map((type) => (
                  <option key={type.id} value={type.id}>
                    {type.name}
                  </option>
                ))}
              </select>
            </div>
            <div className="col-12 col-sm-2">
              <label className="col-form-label filter-label ml-05">
                Location
              </label>
              <select
                className="form-control"
                value={filters.location}
                onChange={(e) => handleFilterChange("location", e.target.value)}
              >
                <option value="">All Locations</option>
                {locations.map((loc) => (
                  <option key={loc.id} value={loc.id}>
                    {loc.name}
                  </option>
                ))}
              </select>
            </div>
            <div className="col-12 col-sm-2">
              <label className="col-form-label filter-label ml-05">
                Status
              </label>
              <select
                className="form-control"
                value={filters.status}
                onChange={(e) => handleFilterChange("status", e.target.value)}
              >
                <option value="">All Status</option>
                <option value="ACTIVE">Active</option>
                <option value="INACTIVE">Inactive</option>
                <option value="MAINTENANCE">Under Maintenance</option>
                <option value="RETIRED">Retired</option>
              </select>
            </div>
            <div className="col-12 col-sm-2 d-flex align-items-end" style={{ paddingTop: "32px" }}>
              <button type="button" className="btn btn-outline-secondary btn-sm" onClick={clearFilters}>
                <i className="fa fa-times me-1"></i> Clear
              </button>
            </div>
          </Form>
        </div>
      </div>

      {/* Error */}
      {error && (
        <div className="alert alert-danger py-2">
          <i className="fa fa-exclamation-circle me-2"></i>
          {error.message || "Error loading machines"}
        </div>
      )}

      {/* Results */}
      <Card>
        <Card.Body className="p-0">
          {machines.length === 0 ? (
            <div className="shipment-list--empty text-center py-5">
              <i className="fa fa-cogs fa-3x mb-3 text-muted"></i>
              <p className="mb-0 text-muted">No machines found</p>
            </div>
          ) : (
            <table className="table table-striped table-bordered datatable mb-0">
              <thead>
                <tr>
                  <th>Machine</th>
                  <th>Type</th>
                  <th>Location</th>
                  <th>Status</th>
                  <th>Next Maintenance</th>
                  <th>Warranty</th>
                  <th className="text-center" style={{ width: "120px" }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {machines.map((machine) => (
                  <tr key={machine.id}>
                    <td>
                      <Link to={`/maintenance/machines/${machine.id}`}>
                        <strong>{machine.name}</strong>
                      </Link>
                      <br />
                      <small className="text-muted">
                        {machine.serial_number && `S/N: ${machine.serial_number}`}
                        {machine.serial_number && machine.model_number && " | "}
                        {machine.model_number && `Model: ${machine.model_number}`}
                      </small>
                    </td>
                    <td>{machine.machine_type_name}</td>
                    <td>{machine.location_name}</td>
                    <td>
                      <span
                        className="badge badge-pill"
                        style={getStatusStyle(machine.status)}
                      >
                        {machine.status_display || machine.status}
                      </span>
                    </td>
                    <td>
                      {machine.next_maintenance_date ? (
                        <>
                          {moment(machine.next_maintenance_date).format("MMM D, YYYY")}
                          {machine.days_until_maintenance !== null && machine.days_until_maintenance < 0 && (
                            <span
                              className="badge badge-pill ms-1"
                              style={{ backgroundColor: "#ff5454" }}
                            >
                              {Math.abs(machine.days_until_maintenance)}d overdue
                            </span>
                          )}
                          {machine.is_maintenance_due && machine.days_until_maintenance >= 0 && (
                            <span
                              className="badge badge-pill ms-1"
                              style={{ backgroundColor: "#ffc107", color: "#000" }}
                            >
                              Due
                            </span>
                          )}
                        </>
                      ) : (
                        <span className="text-muted">Not scheduled</span>
                      )}
                    </td>
                    <td>
                      {machine.is_under_warranty ? (
                        <span
                          className="badge badge-pill"
                          style={{ backgroundColor: "#198754" }}
                        >
                          <i className="fa fa-shield me-1"></i>
                          {moment(machine.warranty_expiration).format("MMM YYYY")}
                        </span>
                      ) : machine.warranty_expiration ? (
                        <span className="badge badge-pill" style={{ backgroundColor: "#6c757d" }}>
                          Expired
                        </span>
                      ) : (
                        <span className="text-muted">N/A</span>
                      )}
                    </td>
                    <td className="text-center">
                      <Link
                        to={`/maintenance/machines/${machine.id}`}
                        className="btn btn-sm btn-outline-primary me-1"
                        title="View"
                      >
                        <i className="fa fa-eye"></i>
                      </Link>
                      <Link
                        to={`/maintenance/machines/${machine.id}/edit`}
                        className="btn btn-sm btn-outline-secondary me-1"
                        title="Edit"
                      >
                        <i className="fa fa-pencil"></i>
                      </Link>
                      <Link
                        to={`/maintenance/records/new?machine=${machine.id}`}
                        className="btn btn-sm btn-outline-success"
                        title="Schedule"
                      >
                        <i className="fa fa-calendar-plus-o"></i>
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Card.Body>
      </Card>

      {machines.length > 0 && (
        <div className="text-muted small mt-2">
          Showing {machines.length} machine{machines.length !== 1 ? "s" : ""}
        </div>
      )}
    </div>
  );
};

export default MachinesList;
